<template>
    <div>
        <b-card-code id="card-progreso">
            <h4 class="mb-2">Progreso de Fabricación</h4>

            <!-- Tarjetas de Pedidos -->
            <div class="pedidos-cards mb-3">
                <div
                    v-for="pedido in listadoPedidos"
                    :key="pedido.id"
                    class="pedido-card"
                    :class="{ 'pedido-card-selected': pedidoSeleccionado && pedidoSeleccionado.id === pedido.id }"
                    @click="seleccionarPedido(pedido)"
                >
                    <div class="pedido-card-codigo">{{ pedido.codigo }}</div>
                    <div class="pedido-card-fecha">{{ pedido.fecha }}</div>
                    <b-badge :variant="getVariantEstado(pedido.estado)" class="pedido-card-estado">{{ pedido.estado }}</b-badge>
                </div>
            </div>

            <!-- Tabla de Items -->
            <b-table
                v-if="itemsPedido.length > 0"
                :items="itemsPedido"
                :fields="columnasItems"
                class="table table-bordered"
                small
                striped
            >
                <template #cell(nombre)="data">
                    <strong>{{ getNombreProducto(data.item.sku) }}</strong>
                    <br><small class="text-muted">{{ data.item.sku }}</small>
                </template>

                <template #cell(corte)="data">
                    <div class="d-flex align-items-center">
                        <span class="mr-2"><b>{{ data.item.cantidadCorte }}</b> / {{ data.item.cantidadTotal }}</span>
                        <b-form-input
                            v-model.number="data.item.inputCorte"
                            type="number"
                            size="sm"
                            style="width: 60px"
                            min="1"
                            placeholder="+N"
                            @wheel.prevent
                        />
                        <b-button
                            variant="outline-success"
                            size="sm"
                            class="ml-1 btn-icon"
                            @click="incrementarProgreso(data.item, 'cantidadCorte')"
                            :disabled="!data.item.inputCorte || data.item.inputCorte < 1"
                        >
                            <i class="fa-solid fa-plus"></i>
                        </b-button>
                    </div>
                    <b-progress :value="data.item.cantidadCorte" :max="data.item.cantidadTotal" height="5px" class="mt-1" variant="info"></b-progress>
                    <small class="text-danger" v-if="data.item.faltaCorte > 0">Faltan: {{ data.item.faltaCorte }}</small>
                    <small class="text-success" v-else><i class="fa-solid fa-check"></i> Completo</small>
                </template>

                <template #cell(aparado)="data">
                    <div class="d-flex align-items-center">
                        <span class="mr-2"><b>{{ data.item.cantidadMano }}</b> / {{ data.item.cantidadTotal }}</span>
                        <b-form-input
                            v-model.number="data.item.inputMano"
                            type="number"
                            size="sm"
                            style="width: 60px"
                            min="1"
                            placeholder="+N"
                            @wheel.prevent
                        />
                        <b-button
                            variant="outline-success"
                            size="sm"
                            class="ml-1 btn-icon"
                            @click="incrementarProgreso(data.item, 'cantidadMano')"
                            :disabled="!data.item.inputMano || data.item.inputMano < 1"
                        >
                            <i class="fa-solid fa-plus"></i>
                        </b-button>
                    </div>
                    <b-progress :value="data.item.cantidadMano" :max="data.item.cantidadTotal" height="5px" class="mt-1" variant="warning"></b-progress>
                    <small class="text-danger" v-if="data.item.faltaMano > 0">Faltan: {{ data.item.faltaMano }}</small>
                    <small class="text-success" v-else><i class="fa-solid fa-check"></i> Completo</small>
                </template>

                <template #cell(historial)="data">
                    <b-button
                        variant="outline-info"
                        size="sm"
                        class="btn-icon"
                        @click="verHistorial(data.item)"
                    >
                        <i class="fa-solid fa-clock-rotate-left"></i>
                    </b-button>
                </template>

                <template #cell(estado)="data">
                    <i v-if="data.item.faltaCorte === 0 && data.item.faltaMano === 0"
                       class="fa-solid fa-circle-check text-success fa-lg"></i>
                    <i v-else-if="data.item.cantidadCorte > 0 || data.item.cantidadMano > 0"
                       class="fa-solid fa-clock text-warning fa-lg"></i>
                    <i v-else class="fa-solid fa-circle text-muted fa-lg"></i>
                </template>
            </b-table>

            <div v-else-if="pedidoSeleccionado" class="text-center text-muted py-3">
                No hay items en este pedido
            </div>

            <div v-else class="text-center text-muted py-3">
                Selecciona un pedido para ver su progreso
            </div>

        </b-card-code>

        <!-- Modal Historial -->
        <b-modal
            id="modal-historial"
            ref="modal-historial"
            :title="'Historial - ' + (itemHistorial ? itemHistorial.sku : '')"
            size="lg"
            hide-footer
            centered
        >
            <div v-if="historialCargando" class="text-center py-3">
                <b-spinner variant="primary"></b-spinner>
            </div>
            <b-table
                v-else-if="historialData.length > 0"
                :items="historialData"
                :fields="columnasHistorial"
                small
                striped
            >
                <template #cell(fecha)="data">
                    {{ formatearFecha(data.item.fecha) }}
                </template>
                <template #cell(columna)="data">
                    <b-badge :variant="data.item.columna === 'cantidadCorte' ? 'info' : 'warning'">
                        {{ data.item.columna === 'cantidadCorte' ? 'CORTE' : 'APARADO' }}
                    </b-badge>
                </template>
                <template #cell(cantidad)="data">
                    <span :class="data.item.cantidad < 0 ? 'text-danger' : 'text-success'">{{ data.item.cantidad > 0 ? '+' : '' }}{{ data.item.cantidad }}</span>
                </template>
            </b-table>
            <div v-else class="text-center text-muted py-3">
                No hay historial para este item
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from '@axios'
import { BTable, BFormInput, BFormGroup, BBadge, BProgress, BModal, BSpinner } from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code'

export default {
    components: { BCardCode, BTable, BFormInput, BFormGroup, BBadge, BProgress, BModal, BSpinner },
    data() {
        return {
            columnasItems: [
                { key: "nombre", label: "Producto", thStyle: { width: "25%" } },
                { key: "corte", label: "CORTE", thStyle: { width: "28%" } },
                { key: "aparado", label: "APARADO", thStyle: { width: "28%" } },
                { key: "historial", label: "", thStyle: { width: "8%" } },
                { key: "estado", label: "", thStyle: { width: "8%" } },
            ],
            columnasHistorial: [
                { key: "fecha", label: "Fecha", sortable: true },
                { key: "columna", label: "Sección" },
                { key: "cantidad", label: "Incremento" },
            ],
            listadoPedidos: [],
            pedidoSeleccionado: null,
            itemsPedido: [],
            skuTitulos: {},
            itemHistorial: null,
            historialData: [],
            historialCargando: false,
        }
    },
    methods: {
        async loadPedidos() {
            try {
                axios.post("/uso", { seccion: "progresoTaller" });

                // Cargar títulos de SKU
                const resTitulos = await axios.get("/sku/title");
                resTitulos.data.forEach(item => {
                    this.skuTitulos[item.skuSingle] = item.productTitle;
                });

                // Cargar pedidos
                const response = await axios.get("/taller/pedido/vero");
                this.listadoPedidos = response.data.filter(p => p.estado !== 'Terminado');
            } catch (error) {
                console.error("Error cargando pedidos:", error);
            }
        },
        seleccionarPedido(pedido) {
            this.pedidoSeleccionado = pedido;
            this.cargarItemsPedido();
        },
        async cargarItemsPedido() {
            if (!this.pedidoSeleccionado) {
                this.itemsPedido = [];
                return;
            }
            try {
                // Usar endpoint de faltantes que calcula todo
                const response = await axios.get(`/taller/pedido/vero/faltantes/${this.pedidoSeleccionado.id}`);
                this.itemsPedido = response.data.map(item => ({
                    ...item,
                    inputCorte: null,
                    inputMano: null
                }));
            } catch (error) {
                console.error("Error cargando items:", error);
            }
        },
        getNombreProducto(sku) {
            const lastDash = sku.lastIndexOf('-');
            const raiz = sku.substring(0, lastDash);
            return this.skuTitulos[raiz] || raiz;
        },
        getVariantEstado(estado) {
            if (estado === 'Terminado') return 'success';
            if (estado === 'En proceso') return 'warning';
            return 'light';
        },
        async incrementarProgreso(item, columna) {
            const cantidadNueva = columna === 'cantidadCorte' ? item.inputCorte : item.inputMano;

            if (!cantidadNueva || cantidadNueva < 1) return;

            try {
                const response = await axios.put("/taller/pedido/vero/progreso", {
                    id: item.id,
                    columna: columna,
                    cantidadNueva: cantidadNueva
                });

                this.$swal({
                    icon: 'success',
                    title: 'Guardado',
                    text: `+${cantidadNueva} registrado (${response.data.fecha})`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                });

                // Limpiar input y recargar
                if (columna === 'cantidadCorte') {
                    item.inputCorte = null;
                } else {
                    item.inputMano = null;
                }

                await this.cargarItemsPedido();
            } catch (error) {
                console.error("Error actualizando progreso:", error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo actualizar el progreso',
                });
            }
        },
        async verHistorial(item) {
            this.itemHistorial = item;
            this.historialData = [];
            this.historialCargando = true;
            this.$refs['modal-historial'].show();

            try {
                const response = await axios.get(`/taller/pedido/vero/historial/${item.id}`);
                this.historialData = response.data;
            } catch (error) {
                console.error("Error cargando historial:", error);
            } finally {
                this.historialCargando = false;
            }
        },
        formatearFecha(fecha) {
            if (!fecha) return '';
            const d = new Date(fecha);
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const anio = d.getFullYear();
            const hora = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            return `${dia}-${mes}-${anio} ${hora}:${min}`;
        }
    },
    mounted() {
        this.loadPedidos();
    }
}
</script>

<style lang="scss" scoped>
#card-progreso .card-header {
    display: none;
}

.pedidos-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.pedido-card {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 140px;
    text-align: center;

    &:hover {
        border-color: #7367f0;
        box-shadow: 0 2px 8px rgba(115, 103, 240, 0.2);
    }
}

.pedido-card-selected {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.1);
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.3);
}

.pedido-card-codigo {
    font-weight: bold;
    font-size: 14px;
    color: #333;
}

.pedido-card-fecha {
    font-size: 12px;
    color: #888;
    margin: 4px 0;
}

.pedido-card-estado {
    font-size: 11px;
}
</style>
